import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox } from "@mui/material";
import { Fragment, ReactElement, memo, useCallback } from "react";

import Condition from "../../../Conditional/Condition";
import I18nText from "../../../I18nText/I18nText";
import QuantitySelector from "../quantity/QuantitySelector";
import "./AccordionSelector.scss";
import "./AccordionSelector_flint.scss";
import "./AccordionSelector_kokoro.scss";
import "./AccordionSelector_pdd.scss";
import "./AccordionSelector_shabuDays.scss";
import "./AccordionSelector_wang.scss";
import AccordionSummaryDisplay from "./AccordionSummaryDisplay";
import {
	ACCORDION_CHECKBOX_STYLE,
	ACCORDION_DETAILS_STYLE,
	ACCORDION_STYLE,
	ACCORDION_SUMMARY_STYLE,
} from "./styles";
import { IAccordionConfig } from "./types";
import { useAccordionChangeControl } from "./useAccordionChangeControl";
import { useAccordionQtyCount } from "./useAccordionQtyCount";
import { useAccordionSelectorControl } from "./useAccordionSelectorControl";

interface IProps {
	config: IAccordionConfig;
}

const AccordionSelector = ({
	config: {
		id,
		options,
		label,
		className = "",
		testId = "AccordionSelector",
		selectMultiple = false,
		autoCollapse = true,
		onChange,
		initValues = [],
		isExpanded = false,
		max = 999,
		allowChange = true,
		allowMultiplePerItem = false,
		erroren = "",
		errorcn = "",
		showErrorOnLoad = false,
	},
}: IProps): ReactElement => {
	const { expanded } = useAccordionSelectorControl(id, autoCollapse, isExpanded);

	const { changeHandler, onClick, selected, addSelection, removeSelection, changed } =
		useAccordionChangeControl(id, selectMultiple, onChange, initValues, max);

	const { changeQtyForSelection, maxvalues } = useAccordionQtyCount(
		addSelection,
		removeSelection,
		selected,
		max,
		options
	);

	const showCheckBox = useCallback(
		(id: string) => (
			<Condition check={selectMultiple}>
				<Checkbox
					className="AccordionCheckBox"
					sx={ACCORDION_CHECKBOX_STYLE}
					checked={!!selected.includes(id)}
				/>
			</Condition>
		),
		[selected, selectMultiple]
	);

	return (
		<div className="AccordionGroup">
			<Accordion
				className={`AccordionSelector ${className}`}
				data-testid={testId}
				elevation={0}
				sx={ACCORDION_STYLE}
				disableGutters
				expanded={allowChange ? expanded : false}
				onChange={changeHandler}
			>
				<AccordionSummary
					expandIcon={
						<Condition check={allowChange}>
							<ExpandMore />
						</Condition>
					}
					sx={ACCORDION_SUMMARY_STYLE}
					className="AccordionSelectorSummary"
					data-testid={`${testId}-button`}
				>
					<AccordionSummaryDisplay selected={selected} label={label} options={options} />
				</AccordionSummary>
				<AccordionDetails sx={ACCORDION_DETAILS_STYLE}>
					{options.map(({ id, selection_display, disabled = false }) => {
						const isSelected = selected.includes(id);
						const qtyselect = allowMultiplePerItem && isSelected;

						return (
							<Fragment key={id}>
								<Condition check={qtyselect}>
									<div className="AccordionOption NoClick">
										<div className="AccordionContent">
											{showCheckBox(id)}
											{selection_display}
											<QuantitySelector
												max={maxvalues[id]}
												onChange={changeQtyForSelection(id)}
												initValue={1}
											/>
										</div>
									</div>
								</Condition>
								<Condition check={!qtyselect}>
									<button
										className={`AccordionOption ${isSelected && "Selected"}`}
										onClick={onClick(id, allowMultiplePerItem)}
										disabled={disabled}
									>
										<div className={`AccordionContent`}>
											{showCheckBox(id)}
											{selection_display}
										</div>
									</button>
								</Condition>
							</Fragment>
						);
					})}
				</AccordionDetails>
			</Accordion>
			<Condition check={!!erroren || !!errorcn}>
				<Condition check={showErrorOnLoad || changed}>
					<I18nText enText={erroren} cnText={errorcn} className="AccordionError" />
				</Condition>
			</Condition>
		</div>
	);
};

export default memo(AccordionSelector);
